import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Sparkles, Loader2, Minimize2 } from 'lucide-react';
import { getSupabase } from '../lib/supabase';
import { GoogleGenAI } from '@google/genai';
import { ChatMessage } from '../types';

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && !chatId) {
      const newId = `chat_${Date.now()}`;
      setChatId(newId);
      initChat(newId);
    }
  }, [isOpen, chatId]);

  useEffect(() => {
    if (chatId) {
      const supabase = getSupabase();
      if (!supabase) return;

      const channel = supabase
        .channel(`chat_messages_${chatId}`)
        .on('postgres_changes', { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'chat_messages', 
          filter: `chat_id=eq.${chatId}` 
        }, (payload) => {
          const newMessage = payload.new as any;
          setMessages(prev => {
            if (prev.find(m => m.id === newMessage.id)) return prev;
            return [...prev, {
              id: newMessage.id,
              chatId: newMessage.chat_id,
              content: newMessage.content,
              senderType: newMessage.sender_type,
              createdAt: newMessage.created_at
            }];
          });
        })
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [chatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initChat = async (id: string) => {
    const supabase = getSupabase();
    if (!supabase) return;

    await supabase.from('chats').insert({ id, client_name: 'Visitor' });
    const welcome = "Welcome to Guardian Housing. I am your AI Concierge. How can I assist you with UAE or UK properties today?";
    const msgId = `welcome_${Date.now()}`;
    
    await supabase.from('chat_messages').insert({ 
      id: msgId, chat_id: id, content: welcome, sender_type: 'ai' 
    });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !chatId) return;

    const supabase = getSupabase();
    if (!supabase) return;

    const userText = input;
    const currentInput = input;
    setInput('');

    // Save User Message to Supabase
    await supabase.from('chat_messages').insert({
      id: `user_${Date.now()}`, 
      chat_id: chatId, 
      content: userText, 
      sender_type: 'client'
    });

    // Update Chat Activity
    await supabase.from('chats').update({ 
      last_message_at: new Date().toISOString() 
    }).eq('id', chatId);

    setIsTyping(true);

    try {
      const apiKey = typeof process !== 'undefined' ? process.env.API_KEY : '';
      if (!apiKey) {
        throw new Error("API Key missing");
      }

      const ai = new GoogleGenAI({ apiKey });
      
      const historyContext = messages.slice(-5).map(m => `${m.senderType}: ${m.content}`).join('\n');
      const prompt = `Recent Conversation:\n${historyContext}\nClient Query: ${currentInput}`;

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          systemInstruction: "You are the Guardian Housing AI Concierge. Represent a top-tier UAE luxury real estate brand. Be professional, direct, and sophisticated. Provide guidance on buying, renting, and off-plan investments. If the client mentions high yields or UK property, highlight our specialized UK portfolio (8.5% ROI). When clients request contact or viewings, inform them that a Senior Advisor will reach out immediately.",
        }
      });

      const aiText = response.text || "I'm processing your inquiry. A human advisor will join this chat to assist you further.";

      // Save AI Response to Supabase
      await supabase.from('chat_messages').insert({
        id: `ai_${Date.now()}`, 
        chat_id: chatId, 
        content: aiText, 
        sender_type: 'ai'
      });
    } catch (err) {
      console.error("AI Assistant Error:", err);
      // Fallback message so user isn't stuck
      await supabase.from('chat_messages').insert({
        id: `err_${Date.now()}`, 
        chat_id: chatId, 
        content: "Our AI concierge is currently refining its knowledge. A human advisor has been notified and will be with you shortly.", 
        sender_type: 'ai'
      });
    } finally {
      setIsTyping(false);
    }
  };

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-[100] w-14 h-14 bg-slate-900 text-gold-500 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all border-2 border-gold-500/30 group"
      >
        <MessageSquare size={24} className="group-hover:rotate-12 transition-transform"/>
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-gold-500"></span>
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[100] w-80 md:w-96 h-[500px] bg-white rounded shadow-2xl flex flex-col border-t-4 border-gold-500 animate-fade-in overflow-hidden font-sans">
      {/* Header */}
      <div className="bg-slate-900 p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gold-500/10 rounded-full border border-gold-500/20">
            <Sparkles size={16} className="text-gold-500"/>
          </div>
          <div>
            <h4 className="text-white text-xs font-bold uppercase tracking-widest leading-none">Guardian Concierge</h4>
            <div className="flex items-center gap-1.5 mt-1">
               <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
               <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">Global Support Active</span>
            </div>
          </div>
        </div>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors">
          <Minimize2 size={18}/>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-slate-50/50 no-scrollbar">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.senderType === 'client' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-3 rounded-sm text-xs shadow-sm ${
              msg.senderType === 'client' 
                ? 'bg-slate-900 text-white rounded-br-none' 
                : (msg.senderType === 'agent' ? 'bg-gold-500 text-white rounded-bl-none' : 'bg-white text-slate-800 border border-slate-100 rounded-bl-none')
            }`}>
              {msg.senderType === 'agent' && <div className="text-[8px] font-bold uppercase tracking-widest mb-1 text-white/70">Senior Advisor</div>}
              {msg.content}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white p-3 rounded-sm border border-slate-100 flex gap-1">
              <div className="w-1 h-1 bg-gold-500 rounded-full animate-bounce"></div>
              <div className="w-1 h-1 bg-gold-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
              <div className="w-1 h-1 bg-gold-500 rounded-full animate-bounce [animation-delay:0.4s]"></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 bg-white border-t border-slate-100 flex gap-2">
        <input 
          type="text" 
          placeholder="Ask about properties or investments..." 
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="flex-grow text-xs border border-slate-100 p-3 rounded outline-none focus:border-gold-500 transition-all bg-slate-50"
        />
        <button type="submit" disabled={!input.trim()} className="bg-slate-900 text-gold-500 p-3 rounded hover:bg-gold-500 hover:text-white transition-all disabled:opacity-50">
          <Send size={16}/>
        </button>
      </form>
    </div>
  );
};

export default ChatWidget;